import { CalendarCheckIcon, PencilIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import { useState } from 'react';

import { AsnAction, AsnReceiptStatus, type AsnDTO } from '@invenco/supply-interface';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
  AlertDialogWithActionTrigger,
} from '@/components/alert-dialog-with-action';
import { ButtonSkeleton } from '@/components/button-skeleton';
import { Button } from '@/components/core/button';
import { DialogTrigger } from '@/components/core/dialog';
import { FormDialog } from '@/components/forms/form-dialog';
import { HeaderActions } from '@/components/header';
import { formatDate } from '@/shared/helpers';
import type { Result } from '@/shared/helpers/Result';

import { AsnEditDialogContent } from './asn-edit-dialog-content';

export function AsnActions({
  asn,
  isLoading,
  onRefresh,
  updateAsn,
  bookAsn,
  cancelAsn,
}: {
  asn?: AsnDTO;
  isLoading?: boolean;
  onRefresh: () => Promise<unknown>;
  updateAsn: (input: Partial<AsnDTO>) => Promise<Result>;
  bookAsn: () => Promise<Result>;
  cancelAsn: () => Promise<Result>;
}) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const canEdit = Boolean(asn?.allowableActions?.includes(AsnAction.EDIT));
  const canBook = !!asn?.allowableActions?.includes(AsnAction.BOOK);
  const canCancelAsn =
    !!asn?.allowableActions?.includes(AsnAction.CANCEL) &&
    asn?.receiptStatus !== AsnReceiptStatus.PROCESSING; // NOTE: this is a condition only for the seller

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <HeaderActions>
      {isLoading ? (
        <>
          <ButtonSkeleton />
          <ButtonSkeleton />
        </>
      ) : null}
      {!isLoading && canCancelAsn ? (
        <AlertDialogWithAction onAction={cancelAsn}>
          <AlertDialogWithActionTrigger asChild>
            <Button variant="outline">
              <XIcon />
              Cancel
            </Button>
          </AlertDialogWithActionTrigger>
          <AlertDialogWithActionContent>
            <AlertDialogWithActionHeader>
              <AlertDialogWithActionTitle>
                Do you want to procced with the ASN cancellation?
              </AlertDialogWithActionTitle>
            </AlertDialogWithActionHeader>
            <AlertDialogWithActionFooter>
              <AlertDialogWithActionCancel>No</AlertDialogWithActionCancel>
              <AlertDialogWithActionSubmit>Yes</AlertDialogWithActionSubmit>
            </AlertDialogWithActionFooter>
          </AlertDialogWithActionContent>
        </AlertDialogWithAction>
      ) : null}
      {!isLoading && asn && canEdit ? (
        <FormDialog>
          <DialogTrigger asChild>
            <Button variant="outline">
              <PencilIcon />
              Edit
            </Button>
          </DialogTrigger>
          <AsnEditDialogContent asn={asn} onSubmit={updateAsn} />
        </FormDialog>
      ) : null}
      {!isLoading && canBook ? (
        <AlertDialogWithAction onAction={bookAsn}>
          <AlertDialogWithActionTrigger asChild>
            <Button>
              <CalendarCheckIcon />
              Book
            </Button>
          </AlertDialogWithActionTrigger>
          <AlertDialogWithActionContent>
            <AlertDialogWithActionHeader>
              <AlertDialogWithActionTitle>
                Are you sure you want to book this ASN?
              </AlertDialogWithActionTitle>
              <AlertDialogWithActionDescription className="space-y-3">
                <p>
                  Please confirm your booking request to arrive at{' '}
                  <strong>[{asn?.locationName}]</strong>{' '}
                  {asn?.expectedAt ? `on the ${formatDate(asn.expectedAt)}` : ''}.
                </p>
                <p>
                  If you would like to make a change to the arrival date, please click on cancel and
                  edit the arrival date on the form.
                </p>
              </AlertDialogWithActionDescription>
            </AlertDialogWithActionHeader>
            <AlertDialogWithActionFooter>
              <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
              <AlertDialogWithActionSubmit>Book</AlertDialogWithActionSubmit>
            </AlertDialogWithActionFooter>
          </AlertDialogWithActionContent>
        </AlertDialogWithAction>
      ) : null}
      <Button
        size="icon"
        variant="outline"
        aria-label="Refresh"
        loading={isRefreshing}
        disabled={isLoading}
        onClick={() => void refresh()}
      >
        <RefreshCwIcon />
      </Button>
    </HeaderActions>
  );
}
